import { useEffect, useCallback } from "react";
import { applicationSchema, useMatrixClient } from "@medoma/chat/matrixClient";
import type { MatrixClient } from "matrix-js-sdk";
import { ClientEvent } from "matrix-js-sdk";
import { getCenterJwt } from "@/api/Chat";
import { usePatients } from "@/api/Patients";
import { useChatNotifications } from "@medoma/chat/useChatNotifications";
import { useUnreadNotifications } from "@medoma/chat/useUnreadNotifications";
import { activeStatuses, getPatientFullName } from "@medoma/models/patients";
import ChatContext from "@medoma/chat/ChatContext";
import { useEmployees } from "@/api/Employee";
import { useLocation, useNavigate } from "react-router";
import { employeeName } from "@medoma/models/shifts";
import { useUserProfile } from "@/api/Users";
import { useSelectedSites } from "@medoma/shared-employee/selectedSites";
import { useAuth } from "react-oidc-context";
import { useLingui } from "@lingui/react/macro";

const browserSupportsNotificationAPI = "Notification" in window;

const useDebugChat = (matrixClient: MatrixClient | null) => {
  useEffect(() => {
    if (matrixClient === null) {
      return;
    }

    if (import.meta.env.VITE_DEBUG_CHAT) {
      matrixClient.on(ClientEvent.Event, console.log);

      return () => {
        matrixClient.off(ClientEvent.Event, console.log);
      };
    }
  }, [matrixClient]);
};

export const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useLingui();
  const location = useLocation();
  const { countryCode } = useUserProfile();
  const { user } = useAuth();

  if (!user) {
    throw new Error("Should have user when rendering site switcher");
  }

  const selectedSites = useSelectedSites(user.profile.sub);
  const selectedSitesKey = selectedSites ? JSON.stringify(selectedSites) : null;

  const matrixClient = useMatrixClient(
    applicationSchema.enum.Center,
    getCenterJwt,
    selectedSitesKey,
  );
  const { data: patients } = usePatients({
    statuses: activeStatuses,
  });
  const { data: employees } = useEmployees();
  const navigate = useNavigate();

  const unreadNotifications = useUnreadNotifications(matrixClient);

  useDebugChat(matrixClient);

  const getSenderDetails = useCallback(
    (senderId: string | undefined) => {
      if (!senderId) {
        return null;
      }

      const patientDetails = patients?.find(
        (patient) => patient.id === senderId,
      );
      if (patientDetails) {
        return { type: "patient", id: senderId };
      }
      const employeeDetails = employees?.find(
        (employee) => employee.matrixUserId === senderId,
      );
      if (employeeDetails) {
        return { type: "employee", id: employeeDetails.id.toString() };
      }
      return null;
    },
    [employees, patients],
  );

  const shouldAlert = useCallback(
    (senderId: string | undefined) => {
      if (!senderId) {
        return false;
      }

      const senderDetails = getSenderDetails(senderId);
      if (senderDetails === null) {
        return false;
      }

      const { type, id } = senderDetails;

      if (type === "patient") {
        const browserIsInFocus = document.hasFocus();
        const hasPatientsChatPanelOpen = location.pathname.endsWith(
          `${id}/communication`,
        );
        return !browserIsInFocus || !hasPatientsChatPanelOpen;
      }

      if (type === "employee") {
        const browserIsInFocus = document.hasFocus();
        const hasEmployeeChatPanelOpen = location.pathname.includes(`/chat`);
        return !browserIsInFocus || !hasEmployeeChatPanelOpen;
      }

      return false;
    },
    [location.pathname, getSenderDetails],
  );

  const createDesktopNotification = useCallback(
    ({
      type,
      id,
      name,
    }: {
      type: "employee" | "patient";
      id: string | number;
      name: string;
    }) => {
      const typeOfUser = type === "employee" ? t`kollega` : t`patient`;
      const notif = new Notification(
        t`Nytt meddelande från ${name} (${typeOfUser})`,
        {
          body: t`Tryck för att öppna chatten`,
          data: { id },
        },
      );
      notif.onclick = function () {
        void navigate(
          `/commandcenter/${type === "employee" ? `chat/${id}` : `patients/${id}/communication`}`,
        );
        window.focus();
      };
    },
    [navigate, t],
  );

  const onAlert = useCallback(
    (senderId: string | undefined) => {
      if (!browserSupportsNotificationAPI) {
        return;
      }
      if (Notification.permission !== "granted") {
        return;
      }

      const patientSender = patients?.find(
        (patient) => patient.id === senderId,
      );
      if (patientSender) {
        createDesktopNotification({
          type: "patient",
          id: patientSender.id,
          name: getPatientFullName(patientSender.name),
        });
      }
      const employeeSender = employees?.find(
        (employee) => employee.matrixUserId === senderId,
      );
      if (employeeSender) {
        createDesktopNotification({
          type: "employee",
          id: employeeSender.id,
          name: employeeName(employeeSender, {
            countryCode,
            viewer: "colleague",
          }),
        });
      }
    },
    [patients, employees, createDesktopNotification, countryCode],
  );

  useChatNotifications(matrixClient, shouldAlert, onAlert);

  return (
    <ChatContext.Provider
      value={{
        matrixClient,
        unreadNotifications,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
